import { AxiosError } from 'axios';

/**
 * Shown for a conflict-class failure (404/409) when the server returned no clean
 * message — the action lost a race, the table has been refetched, and the row or
 * dialog says so calmly rather than as a generic failure (TDEV-140).
 */
export const ALREADY_CHANGED_MESSAGE = 'This already changed — refreshed to the latest.';

/** The backend's user-facing reason, when it sent one we can show verbatim. */
function serverMessage(error: unknown): string | null {
  if (error instanceof AxiosError) {
    const message = (error.response?.data as { message?: unknown } | undefined)?.message;
    if (typeof message === 'string' && message.trim()) return message.trim();
  }
  return null;
}

/**
 * A `404` (the target is gone) or `409` (it is already in the requested state, or
 * was accepted/changed by someone else) means the roster moved under us. Callers
 * refetch on this so the table self-corrects to server truth; on any other
 * failure (network, a permission 403, opaque 5xx) the row is left unchanged.
 */
export function isAlreadyChangedError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;
  const status = error.response?.status;
  return status === 404 || status === 409;
}

/**
 * The message to surface for a failed admin-management action. The backend's own
 * reason (the cap-reached line on an expired revive, "already accepted" on a
 * revoke, "A tenant must keep at least one active Super Admin." on a suspend) is
 * shown verbatim — but only for a client error (4xx), where the message is a
 * deliberate user-facing reason. A conflict (404/409) with no clean message falls
 * back to the calm already-changed notice. A 5xx body, a network error, or
 * anything else falls back to the per-action retry line the caller supplies, so
 * opaque technical text (e.g. a generic "Internal server error") is never exposed.
 */
export function getActionErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    if (status !== undefined && status >= 400 && status < 500) {
      const fromServer = serverMessage(error);
      if (fromServer) return fromServer;
      if (status === 404 || status === 409) return ALREADY_CHANGED_MESSAGE;
    }
  }
  return fallback;
}
